import React, { Fragment } from 'react';
import cx from 'classnames';

import type { Components } from 'react-markdown/src/ast-to-react';

import { TABLET_MIN } from 'libs/universal/utils/device';
import styles from './MarkdownWrapper.module.less';
import ContentfulResponsivePicture from '../ContentfulResponsivePicture';
import { hasImage, isPhotoCreditParagraph } from '../parse-util';

export enum NodeTypes {
  p = 'p',
  a = 'a',
  img = 'img',
  iFrame = 'iframe',
}

type Renderer<T> = (props: T) => React.ReactElement<T>;

type LinkReference = {
  children: React.ReactNode;
  href: string;
  id?: string;
};

type Paragraph = {
  children: React.ReactNode;
};

type Image = {
  src: string;
  alt: string;
};

type iFrame = {
  title: string;
  src: string;
};

export const linkReferenceRenderer: Renderer<LinkReference> = reference => {
  // Check for null and undefined but skip empty strings since those are link targets
  if (reference.href == null) {
    return <React.Fragment>[{reference.children}]</React.Fragment>;
  }
  const target = reference.href.slice(0, 1) === '#' ? undefined : '_blank';
  return (
    <a id={reference.id} href={reference.href} target={target}>
      {reference.children}
    </a>
  );
};

const paragraphRenderer: Renderer<Paragraph> = ({ children }) => {
  const childrenArr = React.Children.toArray(children);

  if (hasImage(childrenArr)) {
    // case where text in same div as image not photo credit
    const applyPhotoCreditStyling = isPhotoCreditParagraph(childrenArr);
    return (
      <div
        className={cx(styles.image, styles.topSpacing, styles.bottomSpacing, {
          [styles.photoCredit]: applyPhotoCreditStyling,
          [styles.pStyling]: !applyPhotoCreditStyling,
        })}
      >
        {children}
      </div>
    );
  }
  // case where photo credit in standalone paragraph following image
  if (isPhotoCreditParagraph(childrenArr)) {
    return (
      <div className={cx(styles.photoCredit, styles.bottomSpacing, styles.topSpacing)}>
        {children}
      </div>
    );
  }

  return <p>{children}</p>;
};

const imageRenderer: Renderer<Image> = reference => {
  return (
    <ContentfulResponsivePicture
      className={cx(styles.image)}
      src={reference.src}
      alt={reference.alt}
      breakpointWidthMap={{ 1: 380, [TABLET_MIN]: 680 }}
      loading="lazy"
    />
  );
};

const iframeRenderer: Renderer<iFrame> = reference => {
  const { title, ...rest } = reference;
  const iframeComponent = <iframe title={title} {...rest} />;
  // youtube is not responsive so let's make it so
  if (reference.src?.includes('youtube')) {
    return <div className={styles.videoContainer}>{iframeComponent}</div>;
  }

  return iframeComponent;
};

export const removeOuterParagraphRenderer: Renderer<Paragraph> = ({ children }) => {
  return <>{children}</>;
};

export default {
  [NodeTypes.p]: paragraphRenderer,
  [NodeTypes.a]: linkReferenceRenderer,
  [NodeTypes.img]: imageRenderer,
  [NodeTypes.iFrame]: iframeRenderer,
} as Components;
